'use client';
import { BrandDocument } from '@models/brand';
import { CategoryDocument } from '@models/category';
import { PublicUserDocument } from '@models/user';
import Breadcrumb from '@ui/Breadcrumb';
import SafeImage from '@ui/SafeImage';
import { ShopByVehicle } from '@ui/ShopByVehicle';
import ShopYourGarage from '@ui/ShopYourGarage';
import useProductsPage, {
  PageSideFilters,
  PageType,
  TopFilters,
} from '@util/hooks/useProductsPage';
import { Pagination } from '@util/types/paginator';
import SearchControls from 'app/search/components/SearchControls';
import { CuratedListDocument } from 'models/curated_list';
import {
  FacetCounts,
  ProductDocument,
  ProductSortOption,
} from 'models/product';
import { UserBike } from 'models/shared';
import { useCallback, useState } from 'react';
import {
  FaCalendarAlt,
  FaSortAmountDown,
  FaSortAmountUpAlt,
  FaStar,
} from 'react-icons/fa';
import Filters from './filters/Filters';
import { PageMainContent } from './products/PageMainContent';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';

export const sortingOptions: {
  label: string;
  value: ProductSortOption;
  id: string;
  icon: JSX.Element;
}[] = [
  {
    label: 'Newest Listings',
    value: 'newest',
    id: 'newest',
    icon: <FaCalendarAlt className="size-5 text-zinc-400" />,
  },
  {
    label: 'Price: Low to High',
    value: 'priceAsc',
    id: 'priceAsc',
    icon: <FaSortAmountUpAlt className="size-5 text-zinc-400" />,
  },
  {
    label: 'Price: High to Low',
    value: 'priceDesc',
    id: 'priceDesc',
    icon: <FaSortAmountDown className="size-5 text-zinc-400" />,
  },
  {
    label: 'Best Match',
    value: 'bestMatch',
    id: 'bestMatch',
    icon: <FaStar className="size-5 text-zinc-400" />,
  },
  {
    label: 'Sold Date',
    value: 'soldDate',
    id: 'soldDate',
    icon: <FaCalendarAlt className="size-5 text-zinc-400" />,
  },
];

export const sellerTypeOptions = [
  {
    label: 'All',
    value: 'all',
    key: 'all',
  },
  {
    label: 'Individual',
    value: 'individual',
    key: 'individual',
  },
  {
    label: 'Dealer',
    value: 'dealer',
    key: 'dealer',
  },
];

export const itemsPerPageOptions = [
  {
    label: '12',
    value: '12',
    id: '12',
  },
  {
    label: '24',
    value: '24',
    id: '24',
  },
  {
    label: '48',
    value: '48',
    id: '48',
  },
];

export const PageHeader = ({
  pageHeaderTitle,
  pageHeaderBody,
  pageHeaderImage,
  pageHeaderGradient = 'bg-gradient-to-r from-white via-white to-transparent',
  pageHeaderBaseBackground = 'bg-inherit',
  pageHeaderDescription,
}: {
  headerBreadcrumb: { label: string; href: string };
  pageHeaderTitle: string | React.ReactNode;
  pageHeaderBody?: string;
  pageHeaderImage?: string;
  pageHeaderGradient?: string;
  pageHeaderBaseBackground?: string;
  pageHeaderDescription?: string;
}) => {
  return (
    <>
      <div
        className={`relative flex h-fit w-full items-center justify-center overflow-clip py-4 ${pageHeaderBaseBackground} transition-colors sm:justify-start`}
      >
        <div
          className={`relative z-10 flex w-full max-w-content items-center justify-between text-black ${
            pageHeaderImage ? pageHeaderGradient : ''
          }`}
        >
          <div className="px-8 sm:px-0">
            {pageHeaderDescription ? (
              <div className="flex flex-col gap-y-2">
                <h1 className="my-4 text-[2.1rem] font-semibold sm:pl-4">
                  {pageHeaderTitle}
                </h1>

                <p className="text-[1.6rem] sm:pl-4 sm:text-[1.8rem]">
                  {pageHeaderDescription}
                </p>
              </div>
            ) : (
              <h1 className="my-4 text-[1.8rem] font-semibold sm:pl-4 sm:text-[2.1rem] sm:font-medium">
                {pageHeaderTitle}
              </h1>
            )}
            {pageHeaderBody && (
              <div
                className="line-clamp-4"
                dangerouslySetInnerHTML={{
                  __html: pageHeaderBody,
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const BrandHeader = ({ brand }: { brand: BrandDocument }) => {
  return (
    <>
      <div className="relative flex h-fit w-full select-none items-center justify-center overflow-clip transition-colors sm:justify-start">
        <div className=" relative z-10 mx-auto flex min-h-[16rem] w-full max-w-content items-center justify-between px-[1.6rem] sm:min-h-[20rem] sm:px-0 sm:pl-[12rem]">
          <div className="pt-10 sm:mr-[6.4rem]">
            <Breadcrumb
              items={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Brands',
                  href: '/brands',
                },
                {
                  label: brand.name,
                  href: '/brands/' + brand.url,
                  current: true,
                },
              ]}
            />
            <div className="mt-6 flex flex-col gap-[1.6rem] sm:flex-row sm:items-center">
              {brand.image?.startsWith('http') && (
                <div className="relative h-[10rem] w-[10rem] sm:h-[20rem] sm:w-[50rem]">
                  <SafeImage
                    className="mr-7 object-contain"
                    src={brand.image}
                    priority
                    fill
                    alt={brand.name}
                  />
                </div>
              )}

              <div>
                <h1 className="sm:text-header3 text-h2 font-semibold">
                  {brand.name}
                </h1>
                {brand.description && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: brand.description,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface ProductsPageProps {
  categories?: CategoryDocument[];
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  headerBreadcrumb?: { label: string; href: string };
  pageHeaderTitle?: string | React.ReactNode;
  pageHeaderDescription?: string;
  pageHeaderImage?: string;
  pageHeaderGradient?: string;
  pageHeaderBaseBackground?: string;
  pageHeaderBody?: string;
  brand?: BrandDocument;
  pageType: PageType;
  keyword: string | null;
  seller_id: string | null;
  allBrands?: string[];
  children?: React.ReactNode;
  filters?: React.ReactNode;
  controls?: React.ReactNode;
  curated_list?: CuratedListDocument;
  sellers: {
    [key: string]: PublicUserDocument;
  };
  showSearch?: boolean;
  bottomText?: string;
  facet_counts?: FacetCounts;
  mostly_parts?: boolean;
}
const ProductsPage = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  categories,
  pageHeaderTitle,
  pageHeaderDescription,
  pageHeaderImage,
  pageHeaderGradient,
  pageHeaderBaseBackground,
  pageHeaderBody,
  brand,
  pageType,
  keyword,
  seller_id,
  children,
  filters,
  controls,
  curated_list,
  headerBreadcrumb,
  facet_counts,
  allBrands,
  bottomText,
  showSearch,
  mostly_parts,
}: ProductsPageProps) => {
  const {
    buttonToggles,
    sideFilters,
    topFilters,
    handleSideFiltersChange,
    handleSortChange,
    handleSearchChange,
    setDrawerOpen,
    isFetching,
    setIsFetching,
    clearAllFilters,
  } = useProductsPage({
    current_cat: null,
    keyword,
    category: null,
    category1: null,
    category2: null,
    category3: null,
    results: initialProducts,
    pageInfo: initialPageInfo,
    initialSideFilters,
    initialTopFilters,
    color: initialSideFilters.color,
    categories: categories ?? [],
    is_featured: pageType === 'featured',
    on_sale: pageType === 'onsale',
    is_auction: pageType === 'auctions',
    curated_list:
      curated_list && pageType === 'curated' ? curated_list.slug : null,
    seller_id,
    brands: [],
    facet_counts: facet_counts,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onBikeComplete = useCallback(
    (bike: UserBike | null) => {
      if (bike === null) {
        handleSideFiltersChange({
          ...sideFilters,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
          bikeTypes: [],
        });
        return;
      }

      if (!bike.year || !bike.make || !bike.model) return;

      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [`${bike.year}`],
        bikeMakes: [bike.make],
        bikeModels: [bike.model!],
        bikeTypes: [bike.type],
      });
    },
    [handleSideFiltersChange, sideFilters]
  );

  const handleFilterRemove = useCallback(
    (filter: { key: string; value: any }) => {
      const { key, value } = filter;
      const newFilters = { ...sideFilters };
      if (Array.isArray(newFilters[key as keyof typeof newFilters])) {
        (newFilters[key as keyof typeof newFilters] as any[]) = (
          newFilters[key as keyof typeof newFilters] as any[]
        ).filter((v) => v !== value);
      } else if (
        typeof newFilters[key as keyof typeof newFilters] === 'object' &&
        newFilters[key as keyof typeof newFilters] !== null
      ) {
        delete (
          newFilters[key as keyof typeof newFilters] as { [key: string]: any }
        )[value];
      } else {
        delete newFilters[key as keyof typeof newFilters];
      }
      handleSideFiltersChange(newFilters);
    },
    [sideFilters, handleSideFiltersChange]
  );

  const {
    handleFilterChange,
    handleCategoryChange,
    handleBikeSelection,
    handleSizeChange,
    handlePriceRangeChange,
  } = useCategoryFilters({
    filterState: sideFilters,
    setFilterState: handleSideFiltersChange,
  });

  const shouldShowShopByVehicle =
    mostly_parts ||
    !!initialSideFilters.bikeModels?.length ||
    pageType === 'profile';
  return (
    <>
      <div className="w-full sm:mx-auto sm:max-w-content">
        {seller_id ? (
          <></>
        ) : brand ? (
          <BrandHeader brand={brand} />
        ) : headerBreadcrumb && pageHeaderTitle ? (
          <PageHeader
            headerBreadcrumb={headerBreadcrumb}
            pageHeaderTitle={pageHeaderTitle}
            pageHeaderDescription={pageHeaderDescription}
            pageHeaderImage={pageHeaderImage}
            pageHeaderBody={pageHeaderBody}
            pageHeaderGradient={pageHeaderGradient}
            pageHeaderBaseBackground={pageHeaderBaseBackground}
          />
        ) : (
          <></>
        )}
      </div>

      {shouldShowShopByVehicle && (
        <div className="w-full">
          <div className="block w-full sm:hidden">
            <ShopYourGarage
              onBikeComplete={onBikeComplete}
              sideFilters={sideFilters}
              handleFilterRemove={handleFilterRemove}
            />
          </div>

          <div className="hidden w-full sm:block">
            <ShopByVehicle
              onBikeComplete={onBikeComplete}
              sideFilters={sideFilters}
            />
          </div>
        </div>
      )}

      {children}
      <PageMainContent
        view={topFilters.view}
        isFetching={isFetching}
        clearAllFilters={clearAllFilters}
        setIsFetching={setIsFetching}
        products={initialProducts}
        pageInfo={initialPageInfo}
        full={!!seller_id}
        filterState={sideFilters}
        attribution={{
          data_source: 'typesense',
          page: pageType,
          section: `products-${topFilters.view}`,
        }}
        onFiltersChange={handleSideFiltersChange}
        filters={
          <div className="w-[38rem]">
            <Filters
              pageType={pageType}
              filterState={sideFilters}
              handleFilterChange={handleFilterChange}
              handleCategoryChange={handleCategoryChange}
              handleBikeSelection={handleBikeSelection}
              handleSizeChange={handleSizeChange}
              categories={categories}
              mostly_parts={mostly_parts}
              facet_counts={facet_counts}
              allBrands={allBrands}
              handlePriceRangeChange={handlePriceRangeChange}
            />
          </div>
        }
        controls={
          controls || (
            <SearchControls
              keyword={keyword ?? ''}
              handleConditionsChange={(value) =>
                handleFilterChange('conditions', value)
              }
              handleSellerTypeChange={(value) =>
                handleFilterChange('sellerType', value)
              }
              handleSortChange={handleSortChange}
              handleSaleChange={(value) => handleFilterChange('onsale', value)}
              setDrawerOpen={setDrawerOpen}
              topFilters={topFilters}
              showSearch={showSearch}
              pageType={pageType}
              dropdownOpen={dropdownOpen}
              buttonToggles={buttonToggles}
              handleSearchChange={handleSearchChange}
              setDropdownOpen={setDropdownOpen}
              filterState={sideFilters}
              setFilterState={handleSideFiltersChange}
              currentCat={null}
              allBrands={allBrands ?? []}
              facet_counts={facet_counts}
              categories={categories}
              mostly_parts={mostly_parts}
            />
          )
        }
        hidePaginator={false}
      />
      {(curated_list?.bottom_text || bottomText) && (
        <div className="mt-16 w-full border-b border-zinc-200 bg-[#F4F4F4] py-8">
          <div className="w-full sm:mx-auto sm:max-w-content">
            <div className=" w-full gap-[1.2rem] px-8 sm:block sm:px-0">
              <div className="flex gap-x-[1.5rem] py-12 sm:mx-auto sm:max-w-content">
                <div>
                  {curated_list?.title && (
                    <h2 className="mb-2 text-left text-[2rem] font-semibold">
                      {curated_list.title}
                    </h2>
                  )}

                  <div
                    className=" text-zinc-600"
                    dangerouslySetInnerHTML={{
                      __html:
                        (curated_list?.bottom_text ?? bottomText)
                          ?.replaceAll(
                            /{size}/g,
                            `${
                              sideFilters?.numberSizes[0] ||
                              sideFilters?.letterSizes[0]
                                ? `size ${
                                    sideFilters.numberSizes[0] ||
                                    sideFilters.letterSizes[0]
                                  }`
                                : ''
                            }`
                          )
                          .replaceAll(
                            /{brand}/g,
                            `${
                              sideFilters?.brands[0]
                                ? sideFilters.brands[0]
                                : ''
                            }`
                          ) ?? '',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
